import React from "react";
import { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { fetchLeague } from "../api/leagues";
import { getGwChallengeMemberPicks, GwChallengePickRow } from "../api/gwChallenge";
import PageLoader from "../components/PageLoader";
import ClubBadge from "../components/ClubBadge";
import { getClubBadge } from "../data/clubBadges";
import StatusDot from "../components/StatusDot";
import { useExportImage } from "../hooks/useExportImage";
import { safeFilename } from "../utils/shareLeague";
import { card, widePageContainer, secondaryBtn, exportBtn, panelHeading } from "../styles";
import StatBox from "../components/league/StatBox";
import { useIsMobile } from "../hooks/useIsMobile";

interface Props { user: User; }

const tooltipStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  padding: "0.4rem 0.7rem",
  fontSize: "0.78rem",
  color: "var(--color-text)",
};

function BarTooltip({ active, payload }: any) {
  const { t } = useTranslation();
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div style={tooltipStyle}>
      <div style={{ fontWeight: 700 }}>{d.name}</div>
      {d.player && <div style={{ color: "var(--color-text-muted)", fontSize: "0.72rem" }}>{d.player}</div>}
      <div style={{ color: "var(--color-accent)" }}>
        {t("gwChallenge.goalsSuffix", { count: d.goals })}
      </div>
    </div>
  );
}

export default function GwChallengeTeamPage({ user }: Props) {
  const { t } = useTranslation();
  const { id: leagueId, memberId } = useParams<{ id: string; memberId: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [leagueName, setLeagueName] = useState("");
  const [leagueStatus, setLeagueStatus] = useState<string>("");
  const [leagueSeason, setLeagueSeason] = useState("");
  const [rows, setRows] = useState<GwChallengePickRow[]>([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [displayName, setDisplayName] = useState("");
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const [chartTab, setChartTab] = useState<"per-gw" | "cumulative">("per-gw");
  const { status: exportStatus, loading: shareLoading, runExport } = useExportImage();
  const exportHeaderRef = useRef<HTMLDivElement>(null);
  const exportListRef   = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useBreadcrumbs(
    leagueName && displayName
      ? [{ label: "Home", to: "/overview" }, { label: leagueName, to: `/leagues/${leagueId}` }, { label: `${displayName}${displayName.endsWith("s") ? "'" : "'s"} ${t("gwChallenge.selections")}` }]
      : [],
  );

  useEffect(() => {
    if (!leagueId || !memberId) return;
    setLoading(true);
    Promise.all([fetchLeague(user, leagueId), getGwChallengeMemberPicks(user, leagueId, memberId)])
      .then(([league, data]) => {
        setLeagueName(league.name);
        setLeagueStatus((league as any).status ?? "");
        setLeagueSeason((league as any).competition?.season ?? "");
        setRows(data.rows.filter((r) => r.completed));
        setTotalPoints(data.totalPoints);
        setDisplayName(data.displayName);
      })
      .catch(() => setError(t("common.somethingWentWrong")))
      .finally(() => setLoading(false));
  }, [leagueId, memberId]); // eslint-disable-line

  if (loading) return <PageLoader />;
  if (error) return <div style={{ padding: "2rem", color: "var(--color-danger)" }}>{error}</div>;

  const completedRows = rows;

  const hitRows = completedRows.filter((r) => (r.pick?.goals ?? 0) > 0);
  const hitRate = completedRows.length > 0 ? Math.round((hitRows.length / completedRows.length) * 100) : null;
  const avgPerGw = completedRows.length > 0 ? totalPoints / completedRows.length : null;
  let currentStreak = 0;
  for (let i = completedRows.length - 1; i >= 0; i--) {
    if ((completedRows[i].pick?.goals ?? 0) > 0) currentStreak++;
    else break;
  }

  let cumGoals = 0;
  const perGwData = completedRows.map((r) => ({ name: r.gameweekName, goals: r.pick?.goals ?? 0, player: r.pick?.player.name ?? "" }));
  const cumulativeData = [
    { name: t("gwChallenge.startLabel"), goals: 0 },
    ...completedRows.map((r) => { cumGoals += r.pick?.goals ?? 0; return { name: r.gameweekName, goals: cumGoals }; }),
  ];
  const tickInterval = completedRows.length > 15 ? Math.floor(completedRows.length / 10) : 0;

  const handleExport = async (mode: "simple" | "full", action: "download" | "copy") => {
    setShareDropdownOpen(false);
    const els = mode === "full" ? [exportHeaderRef.current, exportListRef.current] : [exportListRef.current];
    await runExport(els, action, `${safeFilename(displayName)}.png`);
  };

  return (
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: "1.5rem 0" }}>

      {/* Header card */}
      <div ref={exportHeaderRef} style={{ ...card, marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
          {[t("gwChallenge.selectionsLabel"), leagueSeason].filter(Boolean).map((s, i) => (
            <React.Fragment key={i}>
              <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)" }}>{s}</span>
              <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
            </React.Fragment>
          ))}
          {leagueStatus && <StatusDot status={leagueStatus} />}
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: "0 0 0.25rem", fontFamily: "var(--font-display)", fontSize: "1.75rem", fontWeight: 800, color: "var(--color-text)" }}>
              {displayName}{displayName.endsWith("s") ? "'" : "'s"} {t("gwChallenge.selections")}
            </h1>
            <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
              {t("gwChallenge.completedOnly")}
            </p>
          </div>
          <div data-no-export style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
            {rows.length > 0 && (
              <div style={{ position: "relative" }}>
                <button style={exportBtn} onClick={() => setShareDropdownOpen((v) => !v)} disabled={shareLoading} title={t("gwChallenge.exportBtn")}>
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2v11M6 9l4 4 4-4"/><path d="M3 14v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"/></svg>
                </button>
                {shareDropdownOpen && (
                  <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setShareDropdownOpen(false)} />
                    <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 10, boxShadow: "0 8px 32px var(--color-shadow)", zIndex: 100, minWidth: 190, overflow: "hidden" }}>
                      <div style={{ padding: "0.35rem 1rem 0.2rem", fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>{t("gwChallenge.simple")}</div>
                      {(["download", "copy"] as const).map((action) => (
                        <button key={action} onClick={() => handleExport("simple", action)} style={{ display: "block", width: "100%", padding: "0.5rem 1rem", background: "none", border: "none", textAlign: "left", fontSize: "0.85rem", color: "var(--color-text)", cursor: "pointer" }}>
                          {action === "download" ? t("gwChallenge.downloadImage") : t("gwChallenge.copyImage")}
                        </button>
                      ))}
                      <div style={{ margin: "0.2rem 1rem", borderTop: "1px solid var(--color-border-subtle)" }} />
                      <div style={{ padding: "0.35rem 1rem 0.2rem", fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>{t("gwChallenge.full")}</div>
                      {(["download", "copy"] as const).map((action) => (
                        <button key={action} onClick={() => handleExport("full", action)} style={{ display: "block", width: "100%", padding: "0.5rem 1rem", background: "none", border: "none", textAlign: "left", fontSize: "0.85rem", color: "var(--color-text)", cursor: "pointer" }}>
                          {action === "download" ? t("gwChallenge.downloadImage") : t("gwChallenge.copyImage")}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {exportStatus && (
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 100, whiteSpace: "nowrap", padding: "0.4rem 0.7rem", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, background: "var(--color-surface)", border: `1px solid ${exportStatus.type === "error" ? "var(--color-danger)" : "var(--color-border)"}`, color: exportStatus.type === "error" ? "var(--color-danger)" : "var(--color-text)", boxShadow: "0 8px 32px var(--color-shadow)" }}>
                    {exportStatus.message}
                  </div>
                )}
              </div>
            )}
            <Link to={`/leagues/${leagueId}`} style={{ ...secondaryBtn, textDecoration: "none" }}>{t("gwChallenge.backToLeague")}</Link>
          </div>
        </div>

        {/* Stat boxes */}
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginTop: "1rem" }}>
          <StatBox label={t("gwChallenge.scoreLabel")} value={totalPoints} sub={t("gwChallenge.totalGoals")} accent />
          <StatBox label={t("gwChallenge.gameweeksLabel")} value={completedRows.length} sub={t("gwChallenge.played")} mobileHide />
          {hitRate !== null && <StatBox label={t("gwChallenge.hitRateLabel")} value={`${hitRate}%`} sub={t("gwChallenge.hitRateSub")} />}
          {avgPerGw !== null && <StatBox label={t("gwChallenge.avgPerGwLabel")} value={avgPerGw.toFixed(1)} sub={t("gwChallenge.goalsPerGw")} />}
          {currentStreak > 0 && <StatBox label={t("gwChallenge.streakLabel")} value={currentStreak} sub={t("gwChallenge.streakSub")} mobileHide />}
        </div>
      </div>

      {completedRows.length === 0 ? (
        <div ref={exportListRef} style={{ ...card, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
            {t("gwChallenge.noCompletedGameweeks")}
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "340px 1fr", gap: "1.25rem", alignItems: "start" }}>

          {/* Selections list */}
          <div ref={exportListRef} style={{ ...card, padding: 0, overflow: "hidden", ...(isMobile ? {} : { position: "sticky", top: "1rem" }) }}>
            {rows.map((row, idx) => {
              const cfg = row.pick ? getClubBadge(row.pick.player.club) : null;
              const pts = row.pick?.goals ?? null;

              return (
                <div
                  key={row.gameweekId}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "60px 1fr auto",
                    alignItems: "center",
                    gap: "1rem",
                    padding: "0.85rem 1.5rem",
                    borderBottom: idx < rows.length - 1 ? "1px solid var(--color-border-subtle)" : undefined,
                  }}
                >
                  <div style={{ fontFamily: "var(--font-display)", fontSize: "1rem", fontWeight: 800, color: "var(--color-text-dim)" }}>
                    {row.gameweekName}
                  </div>

                  {row.pick ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      {cfg && <ClubBadge config={cfg} size={18} title={row.pick.player.club} />}
                      <div>
                        <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--color-text)" }}>{row.pick.player.name}</div>
                        <div style={{ fontSize: "0.72rem", color: "var(--color-text-muted)" }}>{row.pick.player.club}</div>
                      </div>
                    </div>
                  ) : (
                    <span style={{ fontSize: "0.82rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>{t("gwChallenge.noPlayerSelected")}</span>
                  )}

                  {pts !== null && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700, color: pts > 0 ? "var(--color-success)" : "var(--color-text-muted)", lineHeight: 1 }}>{pts}</div>
                      <div style={{ fontSize: "0.55rem", fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-dim)", letterSpacing: "0.06em" }}>{t("gwChallenge.pts")}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Score progression chart */}
          <div style={{ ...card }}>
            {isMobile && (
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                <StatBox label={t("gwChallenge.gameweeksLabel")} value={completedRows.length} sub={t("gwChallenge.played")} />
                {currentStreak > 0 && <StatBox label={t("gwChallenge.streakLabel")} value={currentStreak} sub={t("gwChallenge.streakSub")} />}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
              <h2 style={{ ...panelHeading, margin: 0 }}>
                {t("gwChallenge.scoreProgression")}
              </h2>
              <div style={{ display: "flex", gap: "0.4rem" }}>
                {(["per-gw", "cumulative"] as const).map((ct) => (
                  <button
                    key={ct}
                    onClick={() => setChartTab(ct)}
                    style={{
                      padding: "3px 12px", borderRadius: 99, border: "1px solid var(--color-border)",
                      background: chartTab === ct ? "var(--color-accent)" : "transparent",
                      color: chartTab === ct ? "var(--color-btn-primary-text)" : "var(--color-text-muted)",
                      fontSize: "0.75rem", fontWeight: 600, cursor: "pointer", fontFamily: "var(--font-body)",
                    }}
                  >
                    {ct === "per-gw" ? t("gwChallenge.perGwTab") : t("gwChallenge.cumulativeTab")}
                  </button>
                ))}
              </div>
            </div>

            {chartTab === "per-gw" ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={perGwData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--color-text-dim)" }} interval={tickInterval} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--color-text-dim)" }} />
                  <Tooltip content={<BarTooltip />} cursor={false} />
                  <Bar dataKey="goals" radius={[3, 3, 0, 0]} minPointSize={3}>
                    {perGwData.map((entry, index) => (
                      <Cell key={index} fill={entry.goals > 0 ? "var(--color-accent)" : "var(--color-border)"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={cumulativeData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--color-text-dim)" }} interval={tickInterval} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--color-text-dim)" }} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [t("gwChallenge.goalsSuffix", { count: v }), t("gwChallenge.totalLabel")]} />
                  <Area type="monotone" dataKey="goals" stroke="var(--color-accent)" fill="var(--color-accent)" fillOpacity={0.15} strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
